import React, { createRef, useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { User } from '@groma-dev/scaffold';
import { Avatar, AccountDetails, parseUserInitials } from '@groma-dev/navigation';
import { FormattedMessage, useIntl } from 'react-intl';
import { BentoBoxIcon, classNames, getTestIdAttribute, Link } from '@groma-dev/blueprint';
import { AppConfig } from '../../../../core/AppConfig';
import styles from './AuthSection.module.scss';

export interface AuthSectionProps {
  handleOpenBento: () => void;
  user: User;
  signOutUser: (e: React.MouseEvent<HTMLLinkElement>) => void;
  menuOpen: boolean;
}

export const AuthSection = observer((props: AuthSectionProps) => {
  const { handleOpenBento, user, signOutUser, menuOpen } = props;
  const { Domains } = AppConfig.Settings;
  const { formatMessage } = useIntl();
  const popoutRef = createRef<HTMLDivElement>();

  const [showAccountDetails, setShowAccountDetails] = useState<boolean>(false);

  const handleHidePopout = (e: any) => {
    if (!e.relatedTarget) setShowAccountDetails(false);

    if (!popoutRef.current?.contains(e.relatedTarget)) {
      setShowAccountDetails(false);
    }
  };

  const handleToggleAccountDetails = () => {
    setShowAccountDetails(!showAccountDetails);
  };

  const handleKeyPress = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      setShowAccountDetails(false);
    }
  };

  useEffect(() => {
    window.addEventListener('keydown', handleKeyPress);

    return () => {
      window.removeEventListener('keydown', handleKeyPress);
    };
  }, []);

  return (
    <>
      <div className={classNames(styles.bentoContainer, menuOpen && styles.hidden)}>
        <button
          className={classNames(styles.bentoIcon)}
          aria-label={formatMessage({
            id: 'header.bentoBox.ariaLabel',
            description: 'Bento Box aria label',
            defaultMessage: 'Open Bento Panel',
          })}
          onClick={handleOpenBento}
          {...getTestIdAttribute('bentoButton')}>
          <BentoBoxIcon />
        </button>
      </div>
      <div role="presentation" className={classNames(styles.accountWrapper)} ref={popoutRef} onBlur={handleHidePopout}>
        <button
          className={classNames(styles.accountButton)}
          onClick={handleToggleAccountDetails}
          {...getTestIdAttribute('accountButton')}>
          <Avatar>{parseUserInitials(user)}</Avatar>
        </button>
        {showAccountDetails && (
          <div className={classNames(styles.accountPopout)} {...getTestIdAttribute('accountPopout')}>
            <AccountDetails user={user} className={classNames(styles.accountDetails)} testId="accountDetails" />
            <a
              href={Domains.id.baseUrl}
              className={classNames(styles.accountLink)}
              {...getTestIdAttribute('manageAccount')}>
              <FormattedMessage
                id="header.account.manage"
                defaultMessage="Manage Account"
                description="Account Manage Account link"
              />
            </a>
            <Link href="/logout" onClick={signOutUser} className={classNames(styles.accountLink)} testId="signOut">
              <FormattedMessage
                id="header.account.signOut"
                defaultMessage="Sign Out"
                description="Account Sign Out link"
              />
            </Link>
          </div>
        )}
      </div>
    </>
  );
});
